// Feature: docs/features/icd10-coding-search.md
// Feature: docs/features/coding-search-patient-id.md
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  HStack,
  useToast,
  Spinner,
  Badge,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  Link,
  Tooltip
} from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const CodingSearchIcd10 = () => {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  
  // Summary Data State
  const [summaryTimeframe, setSummaryTimeframe] = useState('200'); // '200' or '24h'
  const [summaryData, setSummaryData] = useState({
    payers: [],
    carriers: [],
    inputCodes: [],
    reviewedCodes: [],
    aiCodes: [],
    sources: [],
    totalCases: 0
  });

  const [filters, setFilters] = useState(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return {
      icd10_code: '',
      description: '',
      source: '',
      payer_name: '',
      carrier_name: '',
      start_date: sevenDaysAgo.toISOString().split('T')[0],
      end_date: '',
      limit: 50,
      offset: 0
    };
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (overrideFilters = null) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      // If overrideFilters is an event object (from onClick), ignore it and use state filters
      const activeFilters = (overrideFilters && !overrideFilters.nativeEvent) ? overrideFilters : filters;
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await callApi(`/api/v1/icd10-search?${params.toString()}`, accessToken);
      
      if (!response.ok) {
        throw new Error('Failed to fetch search results');
      }

      const data = await response.json();
      setResults(data.results || []);
      setTotalCount(data.total_count || 0);
      
    } catch (error) {
      console.error("Search error:", error);
      toast({
        title: "Search Failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSummaryData = async (timeframe = summaryTimeframe) => {
    setIsSummaryLoading(true);
    try {
      let url = `/api/v1/icd10-search?limit=200`;
      
      if (timeframe === '24h') {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const startDateStr = yesterday.toISOString().split('T')[0];
        url = `/api/v1/icd10-search?limit=1000&start_date=${startDateStr}`;
      }

      const response = await callApi(url, accessToken);
      if (!response.ok) throw new Error('Failed to fetch summary data');
      
      const data = await response.json();
      const cases = data.results || [];
      
      // Aggregation Maps
      const payerCounts = {};
      const carrierCounts = {};
      const inputCodeCounts = {};
      const reviewedCodeCounts = {};
      const aiCodeCounts = {};
      const sourceCounts = {};

      cases.forEach(c => {
        // Payers
        if (c.payer) {
          payerCounts[c.payer] = (payerCounts[c.payer] || 0) + 1;
        }
        
        // Carriers
        if (c.carrier) {
          carrierCounts[c.carrier] = (carrierCounts[c.carrier] || 0) + 1;
        }

        // ICD-10 Codes
        if (c.consolidated_icd10_analysis && c.consolidated_icd10_analysis.length > 0) {
          c.consolidated_icd10_analysis.forEach(icd => {
            const codeKey = `${icd.code} - ${icd.description?.substring(0, 30)}...`;
            
            // Sources
            if (icd.sources && icd.sources.length > 0) {
              icd.sources.forEach(src => {
                sourceCounts[src] = (sourceCounts[src] || 0) + 1;
              });
            }

            // Input vs AI
            if (icd.status_flags?.is_in_original_text) {
              inputCodeCounts[codeKey] = (inputCodeCounts[codeKey] || 0) + 1;
            }
            if (icd.status_flags?.is_in_best_case) {
              reviewedCodeCounts[codeKey] = (reviewedCodeCounts[codeKey] || 0) + 1;
            }
            if (icd.status_flags?.is_in_optimal_list) {
              aiCodeCounts[codeKey] = (aiCodeCounts[codeKey] || 0) + 1;
            }
          });
        }
      });

      // Convert to sorted arrays
      const sortObj = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));

      setSummaryData({
        payers: sortObj(payerCounts).slice(0, 15),
        carriers: sortObj(carrierCounts).slice(0, 15),
        inputCodes: sortObj(inputCodeCounts).slice(0, 15),
        reviewedCodes: sortObj(reviewedCodeCounts).slice(0, 15),
        aiCodes: sortObj(aiCodeCounts).slice(0, 15),
        sources: sortObj(sourceCounts).slice(0, 15),
        totalCases: cases.length
      });

    } catch (error) {
      console.error("Summary error:", error);
      toast({
        title: "Failed to load summary",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSummaryLoading(false);
    }
  };

  // Removed automatic loading of summary data
  // useEffect(() => {
  //   if (tabIndex === 1 && summaryData.totalCases === 0) {
  //     loadSummaryData();
  //   }
  // }, [tabIndex]);

  const handleTimeframeChange = (e) => {
    const newTimeframe = e.target.value;
    setSummaryTimeframe(newTimeframe);
    // Don't auto-load on timeframe change either, let the user click the button
  };

  const handleSummaryClick = (filterKey, value) => {
    let newFilters = { ...filters, [filterKey]: value };
    
    if (filterKey === 'icd10_code') {
      // Extract just the code part before the dash
      const code = value.split(' - ')[0];
      newFilters = { ...filters, icd10_code: code };
    }

    setFilters(newFilters);
    setTabIndex(0); // Switch to Search tab
    handleSearch(newFilters); // Trigger search immediately
  };

  const handleClear = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    setFilters({
      icd10_code: '',
      description: '',
      source: '',
      payer_name: '',
      carrier_name: '',
      start_date: sevenDaysAgo.toISOString().split('T')[0],
      end_date: '',
      limit: 50,
      offset: 0
    });
    setResults([]);
    setTotalCount(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const SummaryTable = ({ title, data, filterKey }) => (
    <Box bg="white" p={4} borderRadius="md" boxShadow="sm" borderWidth="1px">
      <Heading size="sm" mb={3} color="blue.700">{title}</Heading>
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            <Th>Name</Th>
            <Th isNumeric>Count</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((item, idx) => (
            <Tr 
              key={idx} 
              _hover={{ bg: "blue.50", cursor: "pointer" }}
              onClick={() => handleSummaryClick(filterKey, item.name)}
            >
              <Td fontSize="xs" maxW="200px" isTruncated title={item.name}>{item.name}</Td>
              <Td isNumeric fontWeight="bold">{item.count}</Td>
            </Tr>
          ))}
          {data.length === 0 && (
            <Tr><Td colSpan={2} textAlign="center" color="gray.500">No data</Td></Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="blue" variant="enclosed">
          <TabList>
            <Tab fontWeight="bold">Detailed Search</Tab>
            <Tab fontWeight="bold">Summary Dashboard</Tab>
          </TabList>

          <TabPanels>
            {/* SEARCH TAB */}
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                {/* Filters Section */}
                <Box bg="white" p={5} borderRadius="md" boxShadow="sm" borderWidth="1px">
                  <VStack spacing={4}>
                    <HStack spacing={4} w="100%">
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>ICD-10 Code</Text>
                        <Input 
                          name="icd10_code" 
                          value={filters.icd10_code} 
                          onChange={handleFilterChange} 
                          onKeyDown={handleKeyDown}
                          placeholder="e.g. M54.16" 
                        />
                      </Box>
                      <Box flex={2}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>Description</Text>
                        <Input 
                          name="description" 
                          value={filters.description} 
                          onChange={handleFilterChange} 
                          onKeyDown={handleKeyDown}
                          placeholder="e.g. Radiculopathy" 
                        />
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>Source</Text>
                        <Select name="source" value={filters.source} onChange={handleFilterChange}>
                          <option value="">All Sources</option>
                          <option value="original_document">Original Document</option>
                          <option value="agent_best_case">Best Case</option>
                          <option value="optimal_list">Optimal List</option>
                        </Select>
                      </Box>
                    </HStack>
                    
                    <HStack spacing={4} w="100%">
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>Payer</Text>
                        <Input 
                          name="payer_name" 
                          value={filters.payer_name} 
                          onChange={handleFilterChange} 
                          onKeyDown={handleKeyDown}
                          placeholder="e.g. Medicare" 
                        />
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>Carrier</Text>
                        <Input 
                          name="carrier_name" 
                          value={filters.carrier_name} 
                          onChange={handleFilterChange} 
                          onKeyDown={handleKeyDown}
                          placeholder="e.g. UHC" 
                        />
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>Start Date</Text>
                        <Input 
                          type="date" 
                          name="start_date" 
                          value={filters.start_date} 
                          onChange={handleFilterChange} 
                        />
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>End Date</Text>
                        <Input 
                          type="date" 
                          name="end_date" 
                          value={filters.end_date} 
                          onChange={handleFilterChange} 
                        />
                      </Box>
                    </HStack>
                    <HStack w="100%" justify="flex-end">
                      <Button colorScheme="blue" onClick={handleSearch} isLoading={isLoading} px={8}>
                        Search
                      </Button>
                      <Button variant="outline" onClick={handleClear} px={8}>
                        Clear
                      </Button>
                    </HStack>
                  </VStack>
                </Box>

                {/* Results Section */}
                <Box bg="white" p={5} borderRadius="md" boxShadow="sm" borderWidth="1px" overflowX="auto">
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="md">Results</Heading>
                    <Text fontSize="sm" color="gray.600">
                      {totalCount > 0 ? `Showing ${results.length} of ${totalCount} cases` : 'No results'}
                    </Text>
                  </Flex>

                  {isLoading ? (
                    <Flex justify="center" p={10}>
                      <Spinner size="xl" color="blue.500" />
                    </Flex>
                  ) : results.length > 0 ? (
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Date</Th>
                          <Th>Case Details</Th>
                          <Th>Payer / Carrier</Th>
                          <Th>Documented ICD-10s</Th>
                          <Th>Reviewed ICD-10s</Th>
                          <Th>Optimal ICD-10s</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {results.map((row, idx) => {
                          const inputCodes = row.consolidated_icd10_analysis?.filter(c => c.status_flags?.is_in_original_text) || [];
                          const reviewedCodes = row.consolidated_icd10_analysis?.filter(c => c.status_flags?.is_in_best_case) || [];
                          const aiCodes = row.consolidated_icd10_analysis?.filter(c => c.status_flags?.is_in_optimal_list) || [];

                          return (
                            <Tr key={idx}>
                              <Td whiteSpace="nowrap">
                                {row.procedure_date ? new Date(row.procedure_date).toLocaleDateString() : 'N/A'}
                              </Td>
                              <Td>
                                <Text fontWeight="bold" fontSize="sm">{row.case_description || 'Unknown Case'}</Text>
                                <HStack mt={1}>
                                  <Badge colorScheme={row.claim_type === 'pro' ? 'blue' : 'green'} fontSize="xs">
                                    {row.claim_type?.toUpperCase() || 'STD'}
                                  </Badge>
                                </HStack>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                  ID:{' '}
                                  <Link as={RouterLink} to={`/analyze-case-mn/${row.external_case_id}`} color="blue.500" target="_blank" rel="noopener noreferrer">
                                    {row.external_case_id}
                                  </Link>
                                </Text>
                                {row.external_patient_id && (
                                  <Text fontSize="xs" color="gray.500" mt={1}>
                                    Patient ID:{' '}
                                    <Link as={RouterLink} to={`/ionmform?caseid=${row.external_case_id}`} color="blue.500" target="_blank" rel="noopener noreferrer">
                                      {row.external_patient_id}
                                    </Link>
                                  </Text>
                                )}
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="bold">{row.payer || 'N/A'}</Text>
                                <Text fontSize="xs" color="gray.600">{row.carrier || 'N/A'}</Text>
                              </Td>
                              <Td>
                                <VStack align="start" spacing={2}>
                                  {inputCodes.map((code, cIdx) => (
                                    <Tooltip key={cIdx} label={code.description} placement="top">
                                      <Box p={2} bg="gray.50" borderRadius="md" w="100%" borderWidth="1px">
                                        <HStack mb={1}>
                                          <Badge colorScheme="blue" fontSize="sm">{code.code}</Badge>
                                          <Badge colorScheme={code.medical_necessity?.coverage_status === 'covered' ? 'green' : 'red'} fontSize="xs">
                                            {code.medical_necessity?.coverage_status || 'unknown'}
                                          </Badge>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.600" noOfLines={2}>{code.description}</Text>
                                      </Box>
                                    </Tooltip>
                                  ))}
                                  {inputCodes.length === 0 && <Text fontSize="xs" color="gray.400">None found in text</Text>}
                                </VStack>
                              </Td>
                              <Td>
                                <VStack align="start" spacing={2}>
                                  {reviewedCodes.map((code, cIdx) => (
                                    <Tooltip key={cIdx} label={code.description} placement="top">
                                      <Box p={2} bg="purple.50" borderRadius="md" w="100%" borderWidth="1px" borderColor="purple.200">
                                        <HStack mb={1}>
                                          <Badge colorScheme="purple" fontSize="sm">{code.code}</Badge>
                                          <Badge colorScheme="purple" fontSize="xs">Conf: {code.evidence?.confidence_score || 'N/A'}</Badge>
                                          {!code.status_flags?.is_in_original_text && (
                                            <Badge colorScheme="orange" fontSize="xs">For Review</Badge>
                                          )}
                                        </HStack>
                                        <Text fontSize="xs" color="gray.600" noOfLines={2}>{code.description}</Text>
                                      </Box>
                                    </Tooltip>
                                  ))}
                                  {reviewedCodes.length === 0 && <Text fontSize="xs" color="gray.400">No reviewed codes</Text>}
                                </VStack>
                              </Td>
                              <Td>
                                <VStack align="start" spacing={2}>
                                  {aiCodes.map((code, cIdx) => (
                                    <Tooltip key={cIdx} label={code.description} placement="top">
                                      <Box p={2} bg="green.50" borderRadius="md" w="100%" borderWidth="1px" borderColor="green.200">
                                        <HStack mb={1}>
                                          <Badge colorScheme="green" fontSize="sm">{code.code}</Badge>
                                          <Badge colorScheme="purple" fontSize="xs">Conf: {code.evidence?.confidence_score || 'N/A'}</Badge>
                                          {!code.status_flags?.is_in_original_text && (
                                            <Badge colorScheme="orange" fontSize="xs">For Review</Badge>
                                          )}
                                        </HStack>
                                        <Text fontSize="xs" color="gray.600" noOfLines={2}>{code.description}</Text>
                                      </Box>
                                    </Tooltip>
                                  ))}
                                  {aiCodes.length === 0 && <Text fontSize="xs" color="gray.400">No optimal codes</Text>}
                                </VStack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  ) : (
                    <Flex justify="center" p={10}>
                      <Text color="gray.500">Enter search criteria and click Search to view results.</Text>
                    </Flex>
                  )}
                </Box>
              </VStack>
            </TabPanel>

            {/* SUMMARY TAB */}
            <TabPanel px={0}>
              {isSummaryLoading ? (
                <Flex justify="center" p={10}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : (
                <VStack spacing={6} align="stretch">
                  <Box bg="white" p={5} borderRadius="md" boxShadow="sm" borderWidth="1px">
                    <Flex justify="space-between" align="center">
                      <StatGroup>
                        <Stat>
                          <StatLabel>Cases Analyzed</StatLabel>
                          <StatNumber>{summaryData.totalCases}</StatNumber>
                          <StatHelpText>Click any row below to view detailed cases</StatHelpText>
                        </Stat>
                      </StatGroup>
                      <HStack spacing={4} align="flex-end">
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" mb={1}>Timeframe</Text>
                          <Select size="sm" value={summaryTimeframe} onChange={handleTimeframeChange} w="200px">
                            <option value="200">Last 200 Cases</option>
                            <option value="24h">Last 24 Hours</option>
                          </Select>
                        </Box>
                        <Button 
                          colorScheme="blue" 
                          size="sm" 
                          onClick={() => loadSummaryData(summaryTimeframe)}
                          isLoading={isSummaryLoading}
                        >
                          Load Summary
                        </Button>
                      </HStack>
                    </Flex>
                  </Box>

                  {summaryData.totalCases > 0 ? (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={6}>
                      <SummaryTable title="Top Payers" data={summaryData.payers} filterKey="payer_name" />
                      <SummaryTable title="Top Carriers" data={summaryData.carriers} filterKey="carrier_name" />
                      <SummaryTable title="Top Documented ICD-10s" data={summaryData.inputCodes} filterKey="icd10_code" />
                      <SummaryTable title="Top Reviewed ICD-10s" data={summaryData.reviewedCodes} filterKey="icd10_code" />
                      <SummaryTable title="Top Optimal ICD-10s" data={summaryData.aiCodes} filterKey="icd10_code" />
                      <SummaryTable title="Top Sources" data={summaryData.sources} filterKey="source" />
                    </SimpleGrid>
                  ) : (
                    <Flex justify="center" p={10}>
                      <Text color="gray.500">Click "Load Summary" to view the dashboard.</Text>
                    </Flex>
                  )}
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default CodingSearchIcd10;